import { RequestTopic } from './publicApi';

/**
 * Die Zusatzfragen des Kontaktformulars — der frühere Fragebogen
 * „Betreuungskraft finden".
 *
 * Sie erscheinen **nicht bei jedem Anliegen**. Wer sich bewirbt, hat keinen
 * Pflegegrad, und wer eine allgemeine Frage stellt, soll nicht erst einen
 * Fragebogen ausfüllen müssen. Gefragt wird deshalb nur dort, wo jemand
 * Betreuung für sich oder einen Angehörigen sucht — bei den beiden
 * Betreuungsformen.
 *
 * Bewusst **nicht** bei „Kosten & Angebot": Wer nach dem Preis fragt, will eine
 * Zahl und steht oft ganz am Anfang. Fünf Fragen nach Pflegegrad und Wohnort
 * sind dort eine Hürde vor einer Auskunft, die auch ohne sie zu geben ist.
 */
export const FRAGEBOGEN_TOPICS: RequestTopic[] = ['CARE_24H', 'HOME_CARE'];

/** Stellt dieses Anliegen die Zusatzfragen? */
export const hatFragebogen = (topic: RequestTopic): boolean =>
  FRAGEBOGEN_TOPICS.includes(topic);

export interface FragebogenOption {
  /** Was gespeichert und im Posteingang angezeigt wird. */
  wert: string;
  /**
   * Kürzere Beschriftung für die Schaltfläche. Nur dort sinnvoll, wo die
   * Frage den Zusammenhang schon herstellt — „Welcher Pflegegrad liegt vor?"
   * braucht auf der Schaltfläche keine fünf Mal das Wort „Pflegegrad".
   */
  kurz?: string;
}

export interface FragebogenFrage {
  /** Schlüssel, unter dem die Antwort gespeichert wird. Kleinbuchstaben und Unterstrich. */
  key: string;
  label: string;
  /** Fehlt die Liste, ist es ein Freitextfeld. */
  optionen?: FragebogenOption[];
  platzhalter?: string;
  hinweis?: string;
}

/**
 * Alle Angaben sind freiwillig. Der Fragebogen ist ein Angebot, kein Tor: Eine
 * Anfrage ohne eine einzige Antwort geht genauso raus wie eine vollständige.
 *
 * Die Antworten stehen als Schaltflächen nebeneinander, nicht in Auswahlfeldern.
 * Bei zwei Möglichkeiten kostet ein Auswahlfeld auf dem Telefon drei Schritte —
 * antippen, Liste öffnet, wählen — für etwas, das ein Tipp erledigt, und
 * versteckt die Möglichkeiten bis dahin. Nebenbei entfällt damit ein Eintrag
 * „Keine Angabe": Nichts ausgewählt ist der Startzustand und sieht auch so aus,
 * statt wie eine bereits gegebene Antwort.
 */
export const FRAGEN: FragebogenFrage[] = [
  {
    key: 'fuer_wen',
    label: 'Für wen suchen Sie Unterstützung?',
    optionen: [{ wert: 'Für einen Angehörigen' }, { wert: 'Für mich selbst' }],
  },
  {
    key: 'personen',
    label: 'Wie viele Personen leben im Haushalt?',
    optionen: [{ wert: 'Eine Person' }, { wert: 'Mehrere Personen' }],
  },
  {
    key: 'zeitrahmen',
    label: 'Wie schnell wird Unterstützung gebraucht?',
    optionen: [
      { wert: 'Schnellstmöglich' },
      { wert: 'In den nächsten Wochen' },
      { wert: 'Später' },
      { wert: 'Weiß ich noch nicht' },
    ],
  },
  {
    key: 'pflegegrad',
    label: 'Welcher Pflegegrad liegt vor?',
    optionen: [
      { wert: 'Pflegegrad 1', kurz: '1' },
      { wert: 'Pflegegrad 2', kurz: '2' },
      { wert: 'Pflegegrad 3', kurz: '3' },
      { wert: 'Pflegegrad 4', kurz: '4' },
      { wert: 'Pflegegrad 5', kurz: '5' },
      { wert: 'Noch keiner' },
      { wert: 'Weiß ich nicht' },
    ],
  },
  {
    key: 'ort',
    label: 'Wo wird die Betreuung gebraucht?',
    platzhalter: 'Postleitzahl oder Ort',
    hinweis: 'Damit wir Ihnen sagen können, was bei Ihnen vor Ort möglich ist.',
  },
];

/** Nur beantwortete Fragen — leere Felder haben im Posteingang nichts verloren. */
export function beantwortete(antworten: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(antworten).filter(([, wert]) => wert.trim() !== ''),
  );
}
